import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import backgroundImg from '@sorare/core/src/assets/gridPattern.svg';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { BodyS, HeadlineS, LabelS } from '@sorare/core/src/atoms/typography';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import useCurrentSport from '@sorare/core/src/hooks/useCurrentSport';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { desktopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PlayPageTile } from 'components/lobby/PlayPageTile';

import {
  BlogPostTileQuery,
  BlogPostTileQueryVariables,
} from './__generated__/index.graphql';

const Tile = styled(PlayPageTile)`
  background:
    linear-gradient(
      180deg,
      rgba(var(--c-rgb-neutral-100), 0.8) 0%,
      rgba(var(--c-rgb-neutral-200), 0.4) 25%,
      rgba(var(--c-rgb-neutral-200), 0.8) 75%,
      rgba(var(--c-rgb-neutral-300), 0.9) 100%
    ),
    center url(${backgroundImg});
  z-index: 1;

  @media ${desktopAndAbove} {
    background-size: cover, 20px;
  }
`;

const Tag = styled(LabelS).attrs({
  as: 'p',
  bold: true,
})`
  background: rgba(var(--c-rgb-neutral-1000), 0.17);
  padding: var(--half-unit) var(--unit);
  border-radius: var(--unit);
  width: fit-content;
`;

const Action = styled(BodyS).attrs({ as: 'div', bold: true })`
  display: inline;
  white-space: nowrap;
`;

type Props = {
  gridArea: string;
  isNew?: boolean;
  title: string;
  subtitle: string;
  to: string;
};

const query = gql`
  query BlogPostTileQuery($sport: Sport!) {
    config {
      id
      lobbyTile(sport: $sport, position: CAROUSSEL) {
        id
        sport
        title
        ctaHref
        ctaLabel
        description
        desktopBgImageUrl
        mobileBgImageUrl
      }
    }
  }
` as TypedDocumentNode<BlogPostTileQuery, BlogPostTileQueryVariables>;

export const BlogPostTile = ({
  gridArea,
  isNew,
  title,
  subtitle,
  to,
}: Props) => {
  const {
    flags: { useContentfulForMlbLobby = false },
  } = useFeatureFlags();
  const sport = useCurrentSport();

  const { data, loading } = useQuery(query, {
    variables: { sport },
    skip: !useContentfulForMlbLobby,
  });

  const lobbyTile = data?.config?.lobbyTile;
  return (
    <Skeleton loading={loading}>
      <Tile
        gridArea={gridArea}
        to={lobbyTile?.ctaHref || to}
        tag={
          <Tag>
            {isNew ? (
              <FormattedMessage id="BlogPostTile.new" defaultMessage="New" />
            ) : (
              <FormattedMessage id="BlogPostTile.blog" defaultMessage="Blog" />
            )}
          </Tag>
        }
        footer={
          <Vertical>
            <HeadlineS as="div">{lobbyTile?.title || title}</HeadlineS>
            <BodyS as="div" color="var(--c-neutral-600)">
              <span>{lobbyTile?.description || subtitle}</span>{' '}
              <Action>
                {lobbyTile?.ctaLabel || (
                  <FormattedMessage
                    id="BlogPostTile.readmore"
                    defaultMessage="Read More"
                  />
                )}
              </Action>
            </BodyS>
          </Vertical>
        }
      />
    </Skeleton>
  );
};
